import { FC, useCallback } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import { fDateTime } from 'src/utils/formatTime';
import type { ApprovalRequest } from 'src/components/SupportCase/types';

interface ApprovalCardProps {
  approval: ApprovalRequest;
  /** True once the case has moved on, so the buttons are disabled rather than gone. */
  decided?: boolean;
  busy?: boolean;
  onDecide: (approvalId: string, decision: 'confirm' | 'decline') => void;
}

/**
 * One confirmable change (ENG-4137, decision 1C).
 *
 * The agent never performs a YELLOW write. It proposes one, and this card is the only
 * thing that can authorise it — Cancel left, the action right (destructive-first
 * reading order), and the sentence says what will happen to whose record. Once a
 * decision is recorded the card stays in the timeline as history: it is part of the
 * case, not a transient toast.
 */
const ApprovalCard: FC<ApprovalCardProps> = ({
  approval,
  decided = false,
  busy = false,
  onDecide,
}) => {
  const handleConfirm = useCallback(() => {
    onDecide(approval.approvalId, 'confirm');
  }, [approval.approvalId, onDecide]);

  const handleDecline = useCallback(() => {
    onDecide(approval.approvalId, 'decline');
  }, [approval.approvalId, onDecide]);

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderColor: decided ? 'divider' : 'warning.main' }}
      data-testid={`approval-card-${approval.approvalId}`}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CheckIcon fontSize="small" color="warning" />
          <Typography variant="subtitle2">Needs your confirmation</Typography>
        </Stack>

        <Typography variant="body2">{approval.preview}</Typography>

        {approval.rationale ? (
          <Typography variant="caption" color="text.secondary">
            {approval.rationale}
          </Typography>
        ) : null}

        {decided ? (
          <Alert severity="success" sx={{ py: 0 }}>
            A decision was recorded for this request.
          </Alert>
        ) : (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleDecline}
              disabled={busy}
            >
              Decline change
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleConfirm}
              disabled={busy}
              startIcon={busy ? <CircularProgress size={14} /> : undefined}
            >
              Confirm change
            </Button>
          </Stack>
        )}

        <Box>
          <Typography variant="caption" color="text.secondary">
            Requested {fDateTime(new Date())} · expires in{' '}
            {Math.round(approval.expiresInSeconds / 60)} minutes
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};

export default ApprovalCard;
